import { auth } from '../firebase'; // Import Firebase auth instance

const BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:3800";

/**
 * Previews what would be transferred from one franchisee to another without making changes.
 * @param {string} sourceFseId - The source franchisee ID to transfer from.
 * @param {string} targetFseId - The target franchisee ID to transfer to.
 * @returns {Promise<object>} - The response data containing preview results.
 * @throws {Error} - Throws an error if the preview fails or the user is not authenticated.
 */
export const previewClientTransfer = async (sourceFseId, targetFseId) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/utility/preview-client-transfer`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ sourceFseId, targetFseId }),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    if (responseData.status !== 'success') {
      throw new Error(responseData.message || 'Failed to preview transfer');
    }

    return responseData;
  } catch (error) {
    console.error('Error previewing transfer:', error);
    throw error;
  }
};

/**
 * Transfers clients (households) and their pending jobs from one franchisee to another.
 * @param {string} sourceFseId - The source franchisee ID to transfer from.
 * @param {string} targetFseId - The target franchisee ID to transfer to.
 * @returns {Promise<object>} - The response data containing transfer results.
 * @throws {Error} - Throws an error if the transfer fails or the user is not authenticated.
 */
export const transferClients = async (sourceFseId, targetFseId, householdIds = null) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/utility/transfer-clients`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ sourceFseId, targetFseId, ...(householdIds && householdIds.length > 0 ? { householdIds } : {}) }),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    if (responseData.status !== 'success') {
      throw new Error(responseData.message || 'Failed to transfer clients');
    }

    return responseData;
  } catch (error) {
    console.error('Error transferring clients:', error);
    throw error;
  }
};

/**
 * Updates an FSE Code across the entire system.
 */
export const updateFseCodeSystemWide = async (data) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/utility/update-fse-code`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    return responseData;
  } catch (error) {
    console.error('Error updating FSE code system-wide:', error);
    throw error;
  }
};

/**
 * Preview job schedules from an uploaded XLSX file.
 * Returns categorized results: validSchedules, invalidSchedules, warnings.
 * @param {File} file - The XLSX File object from the browser
 * @returns {Promise<object>}
 */
export const previewJobScheduleImport = async (file) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }

  const idToken = await user.getIdToken();
  const url = `${BASE_URL}/utility/import-job-schedules/preview`;

  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${idToken}` },
    body: formData,
  });

  const responseData = await response.json();

  if (!response.ok) {
    throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
  }

  return responseData;
};

/**
 * Execute the import of selected job schedules.
 * @param {Array} schedules - Array of schedule objects with rowIndex and payload
 * @param {boolean} dryRun - If true, simulates import without writing to DB
 * @returns {Promise<object>}
 */
export const executeJobScheduleImport = async (schedules, dryRun = false) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }

  const idToken = await user.getIdToken();
  const url = `${BASE_URL}/utility/import-job-schedules/execute`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ schedules, dryRun }),
  });

  const responseData = await response.json();

  if (!response.ok) {
    throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
  }

  return responseData;
};

/**
 * Save an import history record to the API (Firestore).
 */
export const saveJobScheduleImportHistory = async ({ fileName, summary, results }) => {
  const user = auth.currentUser;
  if (!user) throw new Error('User not authenticated.');
  const idToken = await user.getIdToken();

  const response = await fetch(`${BASE_URL}/utility/import-job-schedules/history`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${idToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ fileName, summary, results }),
  });
  const responseData = await response.json();
  if (!response.ok) throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
  return responseData;
};

/**
 * Fetch paginated import history list.
 * @param {string|null} cursor - Last doc ID from previous page for pagination
 */
export const fetchImportHistory = async (cursor = null) => {
  const user = auth.currentUser;
  if (!user) throw new Error('User not authenticated.');
  const idToken = await user.getIdToken();

  const params = new URLSearchParams({ limit: '20' });
  if (cursor) params.set('cursor', cursor);

  const response = await fetch(`${BASE_URL}/utility/import-job-schedules/history?${params}`, {
    method: 'GET',
    headers: { 'Authorization': `Bearer ${idToken}` },
  });
  const responseData = await response.json();
  if (!response.ok) throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
  return responseData;
};

/**
 * Fetch a single import history record by ID.
 */
export const fetchImportHistoryDetail = async (id) => {
  const user = auth.currentUser;
  if (!user) throw new Error('User not authenticated.');
  const idToken = await user.getIdToken();

  const response = await fetch(`${BASE_URL}/utility/import-job-schedules/history/${id}`, {
    method: 'GET',
    headers: { 'Authorization': `Bearer ${idToken}` },
  });
  const responseData = await response.json();
  if (!response.ok) throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
  return responseData;
};
